import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spin } from "antd";
import { FiEye, FiRefreshCw, FiTrash2, FiUploadCloud, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import swal from "sweetalert";
import useGet from "../customHooks/useGet";
import usePost from "../customHooks/usePost";
import useDelete from "../customHooks/useDelete";

const normalizeFolderName = (value = "") => value.replaceAll("_", "-");

const DEFAULT_FOLDERS = [
  "blogs-images", "brand-logos", "catalog-banner-image", "customers", "customers-images", "documents",
  "general", "guide-images", "home-banner-images", "home-images", "news",
  "pages-banner-images", "partner-images", "partner-social-images", "popup-banner",
  "products-images", "profile-image", "profile-images", "promotion-images", "properties-images",
  "push-notification-images", "review-images", "reviews-images", "sidebar-banner-images", "sidebar-images",
  "signature-images", "test-ahmed", "test-folder", "testimonial-client-images",
  "utilsImages", "web-content-images",
];

const displayFolderName = (value = "") =>
  value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const buildFolderTabs = (folders = []) => {
  const grouped = new Map();

  folders.forEach((folder) => {
    const folderName = folder?.folder_name || "";
    if (!folderName) return;
    const key = normalizeFolderName(folderName);
    if (!grouped.has(key)) {
      grouped.set(key, { key, label: displayFolderName(key), folderNames: [] });
    }
    const tab = grouped.get(key);
    if (!tab.folderNames.includes(folderName)) tab.folderNames.push(folderName);
  });

  return Array.from(grouped.values());
};

const MediaLibraryModal = ({ open, onClose, onSelect, title = "Media Gallery", initialFolder }) => {
  const [res, apiGet] = useGet();
  const [uploadRes, apiUpload] = usePost();
  const [deleteRes, apiDelete] = useDelete();
  const [activeFolder, setActiveFolder] = useState("");
  const [search, setSearch] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [altText, setAltText] = useState("");
  const [previewImage, setPreviewImage] = useState(null);

  const loadMedia = (folder = activeFolder || normalizeFolderName(initialFolder || "blogs_images")) =>
    apiGet(`innerPages/media?folder_name=${encodeURIComponent(folder)}`);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setSelectedFile(null);
    setAltText("");
    setPreviewImage(null);
    loadMedia(normalizeFolderName(initialFolder || "blogs_images"));
  }, [open]);

  const tabs = useMemo(
    () => buildFolderTabs([...DEFAULT_FOLDERS.map((folder_name) => ({ folder_name })), ...(res?.data?.data?.folders || [])]),
    [res?.data]
  );

  useEffect(() => {
    if (!tabs.length) return;
    const normalizedInitial = normalizeFolderName(initialFolder || "");
    const match = tabs.find((tab) => tab.key === normalizedInitial);
    setActiveFolder((current) => current && tabs.some((tab) => tab.key === current) ? current : match?.key || tabs[0].key);
  }, [tabs, initialFolder]);

  useEffect(() => {
    if (!uploadRes.data) return;
    const { status, message } = uploadRes.data;
    if (status === "false") {
      toast.error(message || "Image upload failed");
      return;
    }
    toast.success(message || "Image uploaded successfully");
    setSelectedFile(null);
    setAltText("");
    loadMedia(activeFolder);
  }, [uploadRes.data]);

  useEffect(() => {
    if (!deleteRes.data) return;
    const { status, message } = deleteRes.data;
    if (status === "false") {
      toast.error(message || "Image delete failed");
      return;
    }
    toast.success(message || "Image deleted successfully");
    setPreviewImage(null);
    loadMedia(activeFolder);
  }, [deleteRes.data]);

  const activeTab = tabs.find((tab) => tab.key === activeFolder) || tabs[0];
  const activeFolderNames = activeTab?.folderNames || [];
  const folderImages = useMemo(() => {
    const query = search.trim().toLowerCase();
    const folders = res?.data?.data?.folders || [];
    return folders
      .filter((folder) => activeFolderNames.includes(folder?.folder_name))
      .flatMap((folder) => (folder?.images || []).map((image) => ({
        ...image,
        folder_name: folder.folder_name,
        normalized_folder_name: normalizeFolderName(folder.folder_name),
      })))
      .filter((image) => !query || image.name?.toLowerCase().includes(query));
  }, [res?.data, activeFolderNames, search]);

  const handleUpload = (event) => {
    event.preventDefault();
    if (!selectedFile || !activeTab) return;
    const formData = new FormData();
    formData.append("image", selectedFile);
    formData.append("folder_name", activeTab.key);
    formData.append("alt_text", altText);
    apiUpload("innerPages/uploadImage", formData);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".webp")) {
      swal("Only .webp images can be uploaded", "Please choose a WEBP image.", "warning");
      event.target.value = "";
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleDelete = (event, image) => {
    event.stopPropagation();
    if (!window.confirm(`Delete ${image.name}?`)) return;
    apiDelete("innerPages/media", {
      folder_name: image.folder_name,
      file_name: image.name,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={980}
      centered
      destroyOnClose
      closeIcon={<FiX size={22} className="text-[#8A8A8A]" />}
      title={<span className="text-[1.05rem] font-MluvkaBold text-[#303030]">{title}</span>}
      styles={{ content: { padding: 0, overflow: "hidden", borderRadius: 16 }, header: { margin: 0, padding: "18px 24px 8px" }, body: { padding: "0 24px 18px" } }}
    >
      <div className="bg-white">
        <div className="flex gap-3 pb-3 pt-1">
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search gallery images" className="h-8 min-w-0 flex-1 rounded-full border border-[#D8D8D8] px-3 text-xs outline-none placeholder:text-[#B8B8B8] focus:border-[#B78A42]" />
          <button type="button" onClick={loadMedia} className="flex h-8 items-center gap-2 rounded-full border border-[#D8D8D8] px-3 text-xs text-[#333] hover:border-[#B78A42]"><FiRefreshCw size={14} /> Refresh</button>
        </div>

        <div className="border-b border-[#E8E8E8]">
          <div className="flex gap-7 overflow-x-auto whitespace-nowrap scrollbar-thin">
            {tabs.map((tab) => <button key={tab.key} type="button" onClick={() => { setActiveFolder(tab.key); setSearch(""); loadMedia(tab.key); }} className={`relative shrink-0 pb-2 pt-2 text-xs transition-colors ${activeFolder === tab.key ? "font-MluvkaBold text-[#B47F32]" : "text-[#333] hover:text-[#B47F32]"}`}>{tab.label}{activeFolder === tab.key && <span className="absolute inset-x-0 bottom-[-1px] h-[2px] bg-[#B47F32]" />}</button>)}
          </div>
        </div>

        <div className="h-[285px] overflow-y-auto py-4 pr-1 scrollbar-thin">
          {res.isLoading ? <div className="grid h-full place-items-center"><Spin /></div> : folderImages.length ? <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 lg:grid-cols-8">{folderImages.map((image) => <div key={`${image.folder_name}-${image.image_path}`} onClick={() => setPreviewImage(image)} className="group relative aspect-square cursor-pointer overflow-hidden rounded-[14px] border border-[#E8E8E8] bg-[#F5F5F5] text-left" title={image.name}><img src={image.image_full_path} alt={image.name} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" /><span className="absolute bottom-1 right-1 grid h-6 w-6 place-items-center rounded-full bg-white/95 text-[#333] shadow-sm"><FiEye size={13} /></span><button type="button" onClick={(event) => handleDelete(event, image)} disabled={deleteRes.isLoading} className="absolute left-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-white/95 text-[#C43D3D] shadow-sm hover:bg-[#C43D3D] hover:text-white disabled:opacity-50" title="Delete image"><FiTrash2 size={13} /></button></div>)}</div> : <div className="grid h-full place-items-center text-sm text-[#999]">No images found in {activeTab?.label || "this folder"}.</div>}
        </div>

        <div className="rounded-[14px] border border-[#DCE5F1] bg-[#F8FAFC] p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-MluvkaBold text-[#40516B]"><FiUploadCloud size={16} /> Upload from system</div>
          <div className="grid gap-2 lg:grid-cols-[1.15fr_1fr_auto]">
            <label className="flex h-9 cursor-pointer items-center overflow-hidden rounded-lg border border-[#DCE5F1] bg-white px-3 text-xs text-[#555]"><span className="truncate">{selectedFile?.name || "Choose .webp image"}</span><input type="file" accept=".webp,image/webp" className="hidden" onChange={handleFileChange} /></label>
            <input value={altText} onChange={(event) => setAltText(event.target.value)} placeholder="Image description" className="h-9 rounded-lg border border-[#D8D8D8] bg-white px-3 text-xs outline-none focus:border-[#B78A42]" />
            <button type="button" onClick={handleUpload} disabled={!selectedFile || uploadRes.isLoading} className="h-9 rounded-full bg-[#B78A42] px-5 text-xs font-MluvkaBold text-white disabled:cursor-not-allowed disabled:opacity-50">{uploadRes.isLoading ? "Uploading..." : "Upload"}</button>
          </div>
        </div>
      </div>
      <Modal
        open={Boolean(previewImage)}
        onCancel={() => setPreviewImage(null)}
        footer={null}
        centered
        width={760}
        title={<span className="text-base font-MluvkaBold">{previewImage?.name}</span>}
      >
        {previewImage && <div className="space-y-4"><button type="button" onClick={() => onSelect(previewImage)} className="block w-full cursor-pointer"><img src={previewImage.image_full_path} alt={previewImage.name} className="max-h-[62vh] w-full rounded-xl object-contain" /></button><div className="flex justify-end gap-2"><button type="button" onClick={() => setPreviewImage(null)} className="rounded-full border border-[#D8D8D8] px-5 py-2 text-sm text-[#444]">Cancel</button><button type="button" onClick={() => onSelect(previewImage)} className="rounded-full bg-[#B78A42] px-5 py-2 text-sm font-MluvkaBold text-white">Select Image</button></div></div>}
      </Modal>
    </Modal>
  );
};

export default MediaLibraryModal;
